// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { type FC, useMemo } from "react";
import { Column, GroupedCombobox, Row, Select, Text } from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";

import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { useFilterableColumnsQuery } from "src/graphql";
import {
  ColumnType,
  type ColumnValue,
  DefaultOperators,
  type FilterableColumn,
  filterSupportedOperators,
  isColumnTypeValue,
  isRelatedColumn,
  isRelatedJourneyEventColumn,
  OperatorOptions,
  OperatorsWithoutValue,
  type PropertyCondition,
  ValueType,
} from "src/types/visual";

import type { FilterProps } from "./condition";
import { ErrorMessage } from "./error-message";
import { Filter } from "./filter";
import { PropertyInput, type PropertyInputProps } from "./property-input";
import { PropertySelector } from "./property-selector";
import {
  createJourneyEventColumn,
  formatValue,
  getOperatorLabel,
  getPropertyNameFromColumn,
  updateConditionOperator,
  useConditionComparison,
} from "./utils";
import { ParameterizeCheckbox } from "./parameterize-checkbox";
import { isJsonPathColumn } from "@hightouch/lib/query/visual/types";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";

type EventPropertyOperatorValueFilterProps = Pick<
  FilterProps<PropertyCondition>,
  "parent" | "condition" | "onUpdate" | "includeParentInPropertyComparison"
> & {
  isDisabled?: boolean;
  loadingSuggestions: boolean;
  operatorError: string | null | undefined;
  showParameterizeCheckbox?: boolean;
  suggestions: PropertyInputProps["suggestions"];
  valueError: string | null | undefined;
  columns?: FilterableColumn[] | undefined; // For PropertyInput if it renders
  onSearchUpdate?: (search: string) => void;
};

export const JourneyEventPropertyOperatorValueFilter: FC<
  Readonly<EventPropertyOperatorValueFilterProps>
> = ({
  condition,
  isDisabled = false,
  loadingSuggestions,
  operatorError,
  parent,
  showParameterizeCheckbox,
  suggestions,
  valueError,
  onUpdate,
  onSearchUpdate,
  columns,
  includeParentInPropertyComparison,
}) => {
  const { selectedEvent } = useQueryBuilderContext();
  const { events, parents } = useFilterableOptionsContext();
  const isParameterized = Boolean(condition.propertyOptions?.parameterize);

  // Journeys only support referencing entry events 1 layer below the root
  const root = parents[parents.length - 1];
  const rootEvents = root?.events ?? events;

  const selectedEventModel = rootEvents.find(
    (event) =>
      event.to_model.id?.toString() === selectedEvent?.eventModelId?.toString(),
  );

  const isPropertySelected =
    isColumnTypeValue(condition.value) &&
    isRelatedJourneyEventColumn(condition.value.property);

  const filterableColumnsQuery = useFilterableColumnsQuery(
    {
      segmentId: selectedEventModel?.to_model.id?.toString() ?? "",
    },
    {
      enabled: Boolean(selectedEventModel?.to_model) && Boolean(selectedEvent), // only fetch if an event is selected
      select: (data) => data.segments_by_pk?.filterable_audience_columns ?? [],
    },
  );
  const eventColumns = filterableColumnsQuery.data ?? [];

  const connectionType = parent?.connection?.definition.type;
  const {
    audienceTraitRhsFilters,
    isTraitSelected,
    supportsColumnComparison,
    propertyType,
  } = useConditionComparison(condition);
  // Memoized so each condition row's Select doesn't see a fresh options
  // array on every parent re-render (this filter is rendered once per
  // condition and re-renders frequently during editing).
  const operatorOptions = useMemo(
    () =>
      propertyType
        ? OperatorOptions[propertyType].filter(
            filterSupportedOperators(
              connectionType,
              isJsonPathColumn(condition.property),
            ),
          )
        : undefined,
    [propertyType, connectionType, condition.property],
  );

  const operatorLabel = getOperatorLabel(condition.operator, propertyType);
  const formattedValue = formatValue(condition, condition.value, {
    showParameterizedLabel: true,
    prefix: isTraitSelected
      ? "entry event trait: "
      : isPropertySelected
        ? "entry event property: "
        : undefined,
  });

  const isEmpty =
    condition.value == undefined &&
    !OperatorsWithoutValue.includes(condition.operator);

  const showEventPropertySelector = Boolean(selectedEvent);

  const allowableOptions = useMemo(
    () => [
      { label: "value", value: "value" },
      ...(showEventPropertySelector
        ? [{ label: "property", value: "property" }]
        : []),
      ...(supportsColumnComparison && audienceTraitRhsFilters
        ? [{ label: "trait", value: "trait" }]
        : []),
    ],
    [
      showEventPropertySelector,
      supportsColumnComparison,
      audienceTraitRhsFilters,
    ],
  );

  return (
    <Filter
      fullWidth
      isDisabled={isDisabled}
      isEmpty={isEmpty}
      content={
        <Row alignItems="flex-start" gap={2}>
          {showParameterizeCheckbox && (
            <ParameterizeCheckbox
              isDisabled={isDisabled}
              isChecked={isParameterized}
              onToggle={(checked) => {
                onUpdate((condition) => {
                  condition.operator =
                    DefaultOperators[propertyType ?? ColumnType.Unknown];
                  condition.value = null;
                  condition.propertyOptions = {
                    ...condition.propertyOptions,
                    parameterize: checked,
                  };
                });
              }}
            />
          )}
          {!isParameterized && (
            <>
              <Select
                removePortal
                isDisabled={isDisabled}
                options={operatorOptions ?? []}
                placeholder="Filter on"
                value={condition.operator}
                width="auto"
                onChange={(value) =>
                  onUpdate((condition) => {
                    const diff = updateConditionOperator(condition, value);
                    Object.entries(diff).forEach(([key, value]) => {
                      condition[key] = value;
                    });
                  })
                }
              />
              {(showEventPropertySelector ||
                (supportsColumnComparison && audienceTraitRhsFilters)) && (
                <Select
                  removePortal
                  isDisabled={isDisabled}
                  options={allowableOptions}
                  value={
                    isTraitSelected
                      ? "trait"
                      : isPropertySelected
                        ? "property"
                        : "value"
                  }
                  width="auto"
                  onChange={(value) => {
                    if (!value) return;
                    onUpdate((condition) => {
                      if (value === "property") {
                        condition.value = {
                          type: ValueType.Column,
                          propertyType: ColumnType.Unknown,
                          property: createJourneyEventColumn({
                            modelId: "",
                            name: "",
                          }),
                        } satisfies ColumnValue;
                      } else if (value === "trait") {
                        condition.value = {
                          type: ValueType.Column,
                          property: {
                            type: "trait",
                            traitDefinitionId: "",
                            conditions: [],
                          },
                        };
                      } else if (value === "value") {
                        condition.value = null;
                      }
                    });
                  }}
                />
              )}
              {isTraitSelected ? (
                <PropertySelector
                  isDisabled={isDisabled}
                  condition={condition}
                  error={valueError}
                  onChange={(valueUpdates) =>
                    onUpdate((condition) => {
                      condition.value = valueUpdates;
                    })
                  }
                  value={condition.value}
                  removePortal
                  parent={parent ?? undefined}
                  selectingTraits={true}
                />
              ) : isPropertySelected && showEventPropertySelector ? (
                <Column>
                  <GroupedCombobox
                    removePortal
                    isInvalid={Boolean(valueError)}
                    isLoading={filterableColumnsQuery.isLoading}
                    optionGroups={[
                      {
                        label: "Entry event columns",
                        options: eventColumns,
                      },
                    ].filter((group) => group.options.length > 0)}
                    optionValue={(option) => option}
                    optionLabel={(option) =>
                      option.alias ??
                      getPropertyNameFromColumn(option.column_reference) ??
                      ""
                    }
                    placeholder="Entry event properties"
                    value={eventColumns.find(
                      (column) =>
                        getPropertyNameFromColumn(column.column_reference) ===
                        (isColumnTypeValue(condition.value) &&
                        isRelatedColumn(condition.value.property) &&
                        "name" in condition.value.property.column
                          ? condition.value.property?.column?.name
                          : undefined),
                    )}
                    width="2xs"
                    onChange={(option) => {
                      if (!option) return;
                      const columnName = getPropertyNameFromColumn(
                        option.column_reference,
                      );
                      if (!columnName) {
                        captureException(
                          `EventPropertyOperatorValueFilter: column name not found for option ${JSON.stringify(option)}`,
                        );
                        return;
                      }
                      onUpdate((condition) => {
                        condition.value = {
                          type: ValueType.Column,
                          propertyType: option.type as ColumnType,
                          property: createJourneyEventColumn({
                            modelId: selectedEventModel?.to_model.id,
                            name: columnName,
                          }),
                        } satisfies ColumnValue;
                      });
                    }}
                  />
                  {valueError && <ErrorMessage>{valueError}</ErrorMessage>}
                </Column>
              ) : (
                <PropertyInput
                  // Journey event property comparison is currently handled separately from the new property comparison
                  allowPropertyComparison={false}
                  isDisabled={isDisabled}
                  condition={condition}
                  error={valueError}
                  loading={loadingSuggestions}
                  suggestions={suggestions}
                  onUpdate={onUpdate}
                  onSearchUpdate={onSearchUpdate}
                  columns={columns}
                  parent={parent ?? undefined}
                  includeParentInPropertyComparison={
                    includeParentInPropertyComparison
                  }
                />
              )}
            </>
          )}
        </Row>
      }
      error={operatorError || valueError}
    >
      <Row gap={1} overflow="hidden">
        {operatorLabel ? (
          <>
            <Text color="text.secondary">{operatorLabel}</Text>{" "}
            <Text color="text.primary" fontWeight="medium">
              {formattedValue}
            </Text>
          </>
        ) : (
          <Text color="text.secondary">Filter on</Text>
        )}
      </Row>
    </Filter>
  );
};
